<template>
  <div class="yt-stack">
    <yt-chart-card
      title="Vues et revenus"
      :subtitle="`Vues sur l'axe de gauche, revenus sur l'axe de droite, par ${granularityLabel}.`"
      :sources="['yt']"
      :columns="trendColumns"
      :rows="buckets"
      :loading="!overview"
      :empty="buckets.length === 0"
    >
      <vue-apex-charts type="line" height="340" :options="trendOptions" :series="trendSeries" />
    </yt-chart-card>
    <yt-chart-card
      title="Vidéos publiées"
      :subtitle="`Nombre de vidéos publiées par ${granularityLabel} sur les chaînes du périmètre.`"
      :sources="['ytdata']"
      :columns="publishedColumns"
      :rows="buckets"
      :loading="!overview"
      :empty="buckets.length === 0"
    >
      <vue-apex-charts type="bar" height="260" :options="publishedOptions" :series="publishedSeries" />
    </yt-chart-card>
  </div>
</template>

<script>
import { computed, toRefs } from '@vue/composition-api'
import VueApexCharts from 'vue-apexcharts'
import YtChartCard from '../YtChartCard.vue'
import useYoutubePerf from '../useYoutubePerf'
import { COLORS, GRANULARITY_LABELS, axisLabel, baseChartOptions, bucketLabel, bucketTitle, fCompact, fEur, fEur2, fEurAxis, fInt } from '../ytFormat'

export default {
  name: 'YtTrendsTab',
  components: { VueApexCharts, YtChartCard },
  props: { active: { type: Boolean, default: false } },
  setup(props) {
    const { active } = toRefs(props)
    const { store, ensureOverview, useTabLoader } = useYoutubePerf()
    useTabLoader(active, ensureOverview)

    const overview = computed(() => store.getters['youtubePerf/getOverview'])
    const buckets = computed(() => overview.value?.series?.buckets || [])
    const granularity = computed(() => overview.value?.series?.granularity || 'day')
    const granularityLabel = computed(() => GRANULARITY_LABELS[granularity.value])
    const categories = computed(() => buckets.value.map((b) => bucketLabel(b, granularity.value)))
    const titles = computed(() => buckets.value.map((b) => bucketTitle(b, granularity.value)))

    const trendSeries = computed(() => [
      { name: 'Vues', type: 'line', data: buckets.value.map((b) => b.views) },
      { name: 'Revenus', type: 'line', data: buckets.value.map((b) => Number(b.revenue.toFixed(2))) },
    ])
    const trendOptions = computed(() => ({
      ...baseChartOptions(),
      colors: [COLORS.views, COLORS.revenue],
      stroke: { width: [3, 2], curve: 'smooth' },
      markers: { size: buckets.value.length > 60 ? 0 : 3, strokeWidth: 2, strokeColors: '#fff', hover: { size: 5 } },
      legend: { show: true, position: 'top', horizontalAlign: 'left', fontWeight: 600 },
      xaxis: { ...baseChartOptions().xaxis, categories: categories.value, rotateAlways: categories.value.length > 12 },
      yaxis: [
        { title: axisLabel('Vues', COLORS.views), labels: { style: { colors: COLORS.views, fontSize: '10px', fontWeight: 600 }, formatter: (v) => fCompact(v) } },
        { opposite: true, title: axisLabel('Revenus (€)', COLORS.revenue), labels: { style: { colors: COLORS.revenue, fontSize: '10px', fontWeight: 600 }, formatter: (v) => fEurAxis(v) } },
      ],
      tooltip: {
        shared: true, intersect: false,
        x: { formatter: (_, { dataPointIndex }) => titles.value[dataPointIndex] || '' },
        y: { formatter: (v, { seriesIndex }) => (seriesIndex === 0 ? fInt(v) : fEur2(v)) },
      },
    }))
    const trendColumns = [
      { key: 'label', label: 'Période', numeric: false, format: (_, row) => bucketTitle(row, granularity.value) },
      { key: 'views', label: 'Vues', format: fInt },
      { key: 'revenue', label: 'Revenus', format: fEur },
      { key: 'rpm', label: 'RPM', format: fEur2 },
    ]

    const publishedSeries = computed(() => [{ name: 'Vidéos publiées', data: buckets.value.map((b) => b.published) }])
    const publishedOptions = computed(() => ({
      ...baseChartOptions(),
      colors: [COLORS.published],
      plotOptions: { bar: { borderRadius: 4, columnWidth: buckets.value.length > 40 ? '90%' : '55%' } },
      xaxis: { ...baseChartOptions().xaxis, categories: categories.value },
      yaxis: { labels: { style: { colors: '#94A3B8', fontSize: '10px', fontWeight: 600 }, formatter: (v) => fInt(v) }, forceNiceScale: true, min: 0 },
      tooltip: { x: { formatter: (_, { dataPointIndex }) => titles.value[dataPointIndex] || '' }, y: { formatter: (v) => fInt(v) } },
    }))
    const publishedColumns = [
      { key: 'label', label: 'Période', numeric: false, format: (_, row) => bucketTitle(row, granularity.value) },
      { key: 'published', label: 'Vidéos publiées', format: fInt },
    ]

    return { overview, buckets, granularityLabel, trendSeries, trendOptions, trendColumns, publishedSeries, publishedOptions, publishedColumns }
  },
}
</script>

<style lang="scss" scoped>
.yt-stack { display: flex; flex-direction: column; gap: $rmr-space-lg; }
</style>
